import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Plus, Trash, LinkSimple, Copy, ArrowBendUpRight, CheckCircle, WarningCircle,
  XCircle, Clock, Scales, ShieldCheck, FlagBanner,
} from "@phosphor-icons/react";

const DBADGE = {
  approved: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle, label: "Approved" },
  changes_requested: { cls: "bg-amber-100 text-amber-800 border-amber-300", icon: WarningCircle, label: "Changes requested" },
  declined: { cls: "bg-red-100 text-red-800 border-red-300", icon: XCircle, label: "Declined" },
  pending: { cls: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock, label: "Pending" },
};
const RSTATUS = {
  in_review: "In review", changes_requested: "Changes requested", action_needed: "Action needed",
  approved: "Approved — ready to finalize", finalized: "Finalized", cancelled: "Cancelled",
};

const Badge = ({ d }) => {
  const b = DBADGE[d] || DBADGE.pending;
  return <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-xs ${b.cls}`}><b.icon size={12} weight="fill" />{b.label}</span>;
};

// Stateless render helpers hoisted to module scope (react/no-unstable-nested-components).
const ChkItem = ({ ok, children }) => (
  <li className="flex items-center gap-2 text-sm">
    {ok ? <CheckCircle size={16} weight="fill" className="text-emerald-600" /> : <XCircle size={16} className="text-muted-foreground" />}
    <span className={ok ? "" : "text-muted-foreground"}>{children}</span>
  </li>
);

const LinkRows = ({ rows, set, testid, upd, rm }) => (
  <div className="space-y-2" data-testid={testid}>
    {rows.map((d, i) => (
      <div key={i} className="grid grid-cols-12 gap-2">
        <Input value={d.label} onChange={(e) => upd(rows, set, i, { label: e.target.value })} placeholder="Label" className="rounded-none col-span-4" />
        <Input value={d.url} onChange={(e) => upd(rows, set, i, { url: e.target.value })} placeholder="https://… (SharePoint link)" className="rounded-none col-span-7" />
        <Button variant="ghost" size="icon" onClick={() => rm(rows, set, i)} className="col-span-1"><Trash size={16} /></Button>
      </div>
    ))}
    <Button variant="outline" size="sm" onClick={() => set([...rows, { label: "", url: "" }])} className="rounded-none"><Plus size={14} className="mr-1" />Add link</Button>
  </div>
);

export default function InvestigationReview({ id, inv, isLead }) {
  const [state, setState] = useState(null);
  const [pool, setPool] = useState([]);
  const [smes, setSmes] = useState([]);
  const [busy, setBusy] = useState("");

  // start-review form
  const [docs, setDocs] = useState([{ label: "", url: "" }]);
  const [reviewers, setReviewers] = useState([]);
  const [rname, setRName] = useState(""); const [remail, setREmail] = useState(""); const [rsave, setRSave] = useState(true);
  const [instructions, setInstructions] = useState("");

  // escalate / finalize / preservation
  const [escId, setEscId] = useState(""); const [mgrName, setMgrName] = useState(""); const [mgrEmail, setMgrEmail] = useState("");
  const [finalLabel, setFinalLabel] = useState("Final PDF"); const [finalUrl, setFinalUrl] = useState("");
  const [sp, setSp] = useState([{ label: "", url: "" }]);
  const [sel, setSel] = useState([{ label: "", url: "" }]);

  const load = useCallback(() => {
    api.get(`/investigations/${id}/review`).then((r) => {
      setState(r.data);
      const p = r.data.preservation || {};
      setSp((p.sharepoint_links?.length ? p.sharepoint_links : [{ label: "", url: "" }]));
      setSel((p.sel_submissions?.length ? p.sel_submissions : [{ label: "", url: "" }]));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/investigations/meta/investigators").then((r) => setPool(r.data)).catch(() => {});
    api.get("/investigations/meta/sme-contacts").then((r) => setSmes(r.data)).catch(() => {});
  }, []);

  if (!state) return <p className="text-sm text-muted-foreground pt-4">Loading review…</p>;
  const review = state.review;
  const chk = state.checklist || {};
  const overdue = state.close_due_date && new Date(state.close_due_date) < new Date() && inv?.status !== "closed";

  // ---- list helpers ----
  const upd = (arr, set, i, patch) => set(arr.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const rm = (arr, set, i) => set(arr.filter((_, j) => j !== i));
  const addReviewer = (r) => { if (r.email && !reviewers.find((x) => x.email === r.email)) setReviewers([...reviewers, r]); };

  const copyLink = (url) => { navigator.clipboard?.writeText(url); toast.success("Sign-off link copied"); };
  const safeHref = (u) => (/^https?:\/\//i.test(u || "") ? u : "#");

  const startReview = async () => {
    const cleanDocs = docs.filter((d) => d.url.trim());
    if (!cleanDocs.length) return toast.error("Add at least one document link.");
    if (!reviewers.length) return toast.error("Add at least one reviewer.");
    setBusy("start");
    try {
      await api.post(`/investigations/${id}/review`, { documents: cleanDocs, reviewers, instructions });
      toast.success("Formal review started — sign-off links generated.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const act = async (fn, label) => {
    setBusy(label); try { await fn(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const escalate = (rid) => act(async () => {
    if (!mgrEmail.trim()) throw { response: { data: { detail: "Enter the manager's email." } } };
    await api.post(`/investigations/${id}/review/escalate`, { reviewer_id: rid, manager_name: mgrName, manager_email: mgrEmail });
    setEscId(""); setMgrName(""); setMgrEmail(""); toast.success("Escalated to manager.");
  }, "escalate");

  const finalize = () => act(async () => {
    await api.post(`/investigations/${id}/review/finalize`, { final_pdf_links: [{ label: finalLabel, url: finalUrl }] });
    toast.success("Review finalized — final PDF saved.");
  }, "finalize");

  const savePreservation = () => act(async () => {
    await api.put(`/investigations/${id}/preservation`, {
      sharepoint_links: sp.filter((x) => x.url.trim()), sel_submissions: sel.filter((x) => x.url.trim()) });
    toast.success("Preservation links saved.");
  }, "preserve");

  const togglePnc = (v) => act(async () => {
    await api.post(`/investigations/${id}/pnc`, { is_pnc: v });
    toast.success(v ? "Marked as PNC and flagged in Risk Matrix." : "PNC cleared.");
  }, "pnc");

  const toggleHold = (v) => act(async () => {
    await api.post(`/investigations/${id}/legal-hold`, { active: v, reason: `Preservation for investigation: ${inv?.title || ""}` });
    toast.success(v ? "Legal hold placed on affected requirement(s)." : "Legal hold released.");
  }, "hold");

  return (
    <div className="pt-4 space-y-6" data-testid="inv-review-tab">
      {/* SLA banner */}
      <div className={`flex items-center gap-2 text-sm px-3 py-2 border ${overdue ? "border-red-300 bg-red-50 text-red-700" : "border-border bg-muted/40"}`} data-testid="inv-sla-banner">
        <Clock size={16} />
        {overdue ? <span><b>Overdue</b> — this investigation passed its 60-day close target ({String(state.close_due_date).slice(0, 10)}). Investigators are notified by email.</span>
          : <span>Close target: <b>{state.close_due_date ? String(state.close_due_date).slice(0, 10) : "—"}</b> (within 60 days of opening).</span>}
      </div>

      {!review || review.status === "cancelled" ? (
        // ---------- START REVIEW ----------
        !isLead ? <p className="text-sm text-muted-foreground">No formal review is in progress. The Lead Investigator starts the review.</p> : (
          <div className="space-y-5" data-testid="start-review-form">
            <div>
              <h3 className="font-semibold text-[#071B3E]">Start a formal review</h3>
              <p className="text-xs text-muted-foreground">Route the secured document link(s) to reviewers for sign-off. Editing & tracked changes happen in the linked document (e.g. SharePoint).</p>
            </div>
            <div>
              <Label>Document links under review</Label>
              <div className="mt-1"><LinkRows rows={docs} set={setDocs} testid="start-docs" upd={upd} rm={rm} /></div>
            </div>
            <div>
              <Label>Reviewers</Label>
              <div className="grid grid-cols-12 gap-2 mt-1">
                <Input value={rname} onChange={(e) => setRName(e.target.value)} placeholder="Name" className="rounded-none col-span-3" data-testid="reviewer-name" />
                <Input value={remail} onChange={(e) => setREmail(e.target.value)} placeholder="Email" className="rounded-none col-span-4" data-testid="reviewer-email" />
                <label className="col-span-3 flex items-center gap-2 text-xs"><Checkbox checked={rsave} onCheckedChange={(c) => setRSave(!!c)} /> Save as SME</label>
                <Button variant="outline" className="rounded-none col-span-2" data-testid="add-reviewer-btn"
                  onClick={() => { if (!remail.trim()) return toast.error("Enter reviewer email."); addReviewer({ name: rname, email: remail, save_as_sme: rsave }); setRName(""); setREmail(""); }}>
                  <Plus size={14} className="mr-1" />Add
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Select onValueChange={(v) => { const p = pool.find((x) => x.id === v); if (p) addReviewer({ name: p.name, email: p.email, user_id: p.id }); }}>
                  <SelectTrigger className="rounded-none h-9"><SelectValue placeholder="Quick add: platform user" /></SelectTrigger>
                  <SelectContent>{pool.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.email})</SelectItem>)}</SelectContent>
                </Select>
                <Select onValueChange={(v) => { const s = smes.find((x) => x.email === v); if (s) addReviewer({ name: s.name, email: s.email, save_as_sme: false }); }}>
                  <SelectTrigger className="rounded-none h-9"><SelectValue placeholder="Quick add: saved SME" /></SelectTrigger>
                  <SelectContent>{smes.length ? smes.map((s) => <SelectItem key={s.email} value={s.email}>{s.name} ({s.email})</SelectItem>) : <div className="px-2 py-1 text-xs text-muted-foreground">No saved SMEs yet</div>}</SelectContent>
                </Select>
              </div>
              {reviewers.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="reviewer-list">
                  {reviewers.map((r, i) => (
                    <div key={i} className="flex items-center justify-between border border-border px-3 py-1.5 text-sm">
                      <span>{r.name || "(no name)"} <span className="text-muted-foreground">{r.email}</span>{r.user_id ? " · user" : " · SME"}</span>
                      <Button variant="ghost" size="icon" onClick={() => setReviewers(reviewers.filter((_, j) => j !== i))}><Trash size={14} /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Instructions to reviewers (optional)</Label>
              <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Use Track Changes / All Markup; return by Friday." className="rounded-none mt-1 min-h-[70px]" />
            </div>
            <Button onClick={startReview} disabled={busy === "start"} className="rounded-none" data-testid="start-review-btn">
              <ShieldCheck size={16} className="mr-1.5" weight="fill" />{busy === "start" ? "Starting…" : "Start formal review"}
            </Button>
          </div>
        )
      ) : (
        // ---------- ACTIVE REVIEW ----------
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-[#071B3E]">Formal review</h3>
              <div className="text-sm text-muted-foreground">Status: <b>{RSTATUS[review.status] || review.status}</b>{review.started_by ? ` · started by ${review.started_by}` : ""}</div>
            </div>
            {isLead && review.status !== "finalized" && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-none" disabled={busy === "cancel"}
                  onClick={() => act(async () => { await api.post(`/investigations/${id}/review/cancel`); toast.success("Review cancelled."); }, "cancel")} data-testid="cancel-review-btn">Cancel review</Button>
              </div>
            )}
          </div>

          {/* documents */}
          <div>
            <div className="text-sm font-semibold mb-1">Documents under review</div>
            <div className="space-y-1" data-testid="review-documents">
              {(review.documents || []).map((d) => (
                <a key={d.id} href={safeHref(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-border px-3 py-2 text-sm text-blue-700 hover:bg-muted/50 break-all"><LinkSimple size={14} />{d.label}</a>
              ))}
            </div>
          </div>

          {/* reviewers */}
          <div>
            <div className="text-sm font-semibold mb-1">Reviewers & sign-off</div>
            <div className="space-y-2" data-testid="review-reviewers">
              {(review.reviewers || []).map((r) => (
                <div key={r.id} className="border border-border p-3 space-y-2" data-testid={`reviewer-${r.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm">{r.name} <span className="text-muted-foreground">{r.email}</span></div>
                    <div className="flex items-center gap-2">
                      <Badge d={r.effective_decision} />
                      <Button variant="outline" size="sm" className="rounded-none" onClick={() => copyLink(r.sign_url)} data-testid={`copy-link-${r.id}`}><Copy size={13} className="mr-1" />Copy link</Button>
                      {isLead && review.status !== "finalized" && r.effective_decision !== "approved" && (
                        <Button variant="outline" size="sm" className="rounded-none" onClick={() => setEscId(escId === r.id ? "" : r.id)} data-testid={`escalate-${r.id}`}><ArrowBendUpRight size={13} className="mr-1" />Escalate</Button>
                      )}
                    </div>
                  </div>
                  {r.comment && <div className="text-xs bg-muted/50 px-2 py-1">“{r.comment}”</div>}
                  {r.escalated_to && (
                    <div className="border-l-2 border-amber-400 pl-3 ml-1 text-sm">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span>Escalated to <b>{r.escalated_to.name}</b> <span className="text-muted-foreground">{r.escalated_to.email}</span></span>
                        <div className="flex items-center gap-2"><Badge d={r.escalated_to.decision} />
                          <Button variant="outline" size="sm" className="rounded-none" onClick={() => copyLink(r.escalated_to.sign_url)}><Copy size={13} className="mr-1" />Copy link</Button></div>
                      </div>
                      {r.escalated_to.comment && <div className="text-xs bg-muted/50 px-2 py-1 mt-1">“{r.escalated_to.comment}”</div>}
                    </div>
                  )}
                  {escId === r.id && (
                    <div className="grid grid-cols-12 gap-2 bg-muted/40 p-2" data-testid="escalate-form">
                      <Input value={mgrName} onChange={(e) => setMgrName(e.target.value)} placeholder="Manager name" className="rounded-none col-span-4" />
                      <Input value={mgrEmail} onChange={(e) => setMgrEmail(e.target.value)} placeholder="Manager email" className="rounded-none col-span-5" />
                      <Button className="rounded-none col-span-3" disabled={busy === "escalate"} onClick={() => escalate(r.id)} data-testid="confirm-escalate-btn">Send to manager</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* finalize */}
          {isLead && review.status !== "finalized" && (
            <div className="border border-border p-3 space-y-2" data-testid="finalize-section">
              <div className="text-sm font-semibold">Finalize</div>
              <p className="text-xs text-muted-foreground">When all reviewers approve, save the final PDF link (export the reviewed document to PDF and store it here).</p>
              <div className="grid grid-cols-12 gap-2">
                <Input value={finalLabel} onChange={(e) => setFinalLabel(e.target.value)} placeholder="Label" className="rounded-none col-span-3" />
                <Input value={finalUrl} onChange={(e) => setFinalUrl(e.target.value)} placeholder="Final PDF link" className="rounded-none col-span-6" data-testid="final-pdf-url" />
                <Button className="rounded-none col-span-3" disabled={review.status !== "approved" || busy === "finalize"} onClick={finalize} data-testid="finalize-btn">
                  {busy === "finalize" ? "Saving…" : "Mark final"}
                </Button>
              </div>
              {review.status !== "approved" && <p className="text-xs text-amber-700">All reviewers must approve (escalate any that decline) before finalizing.</p>}
            </div>
          )}
          {review.status === "finalized" && (
            <div className="border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="finalized-banner">
              Finalized by {review.finalized_by}. Final PDF: {(review.final_pdf_links || []).map((f) => <a key={f.id} href={safeHref(f.url)} target="_blank" rel="noreferrer" className="underline ml-1">{f.label}</a>)}
            </div>
          )}
        </div>
      )}

      {/* ---------- CLOSURE & PRESERVATION (lead) ---------- */}
      {isLead && (
        <div className="border-t border-border pt-5 space-y-5" data-testid="closure-section">
          <h3 className="font-semibold text-[#071B3E]">Closure & records preservation</h3>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm border border-border px-3 py-2" data-testid="pnc-toggle">
              <Checkbox checked={!!state.is_pnc} disabled={busy === "pnc"} onCheckedChange={(c) => togglePnc(!!c)} />
              <FlagBanner size={15} /> Potential Noncompliance (PNC) — flag in Risk Matrix
            </label>
            <Button variant="outline" className="rounded-none" disabled={busy === "hold"} onClick={() => toggleHold(true)} data-testid="legal-hold-btn"><Scales size={15} className="mr-1.5" />Place legal hold</Button>
            <Button variant="ghost" size="sm" className="rounded-none" disabled={busy === "hold"} onClick={() => toggleHold(false)}>Release hold</Button>
            <span className="text-xs text-muted-foreground">Affects {(state.affected_requirement_ids || []).length} requirement(s)</span>
          </div>

          <div>
            <Label>SharePoint evidence links (preservation)</Label>
            <div className="mt-1"><LinkRows rows={sp} set={setSp} testid="sp-links" upd={upd} rm={rm} /></div>
          </div>
          <div>
            <Label>NERC SEL (Secure Evidence Locker) submissions</Label>
            <div className="mt-1"><LinkRows rows={sel} set={setSel} testid="sel-links" upd={upd} rm={rm} /></div>
            <p className="text-xs text-muted-foreground mt-1">Mitigating activities are preserved from the Solutions tab.</p>
          </div>
          <Button onClick={savePreservation} disabled={busy === "preserve"} className="rounded-none" data-testid="save-preservation-btn">{busy === "preserve" ? "Saving…" : "Save preservation links"}</Button>

          <div className="border border-border p-3" data-testid="closure-checklist">
            <div className="text-sm font-semibold mb-2">Closure checklist {chk.can_close && <span className="text-emerald-700">— ready to close</span>}</div>
            <ul className="space-y-1.5">
              <ChkItem ok={chk.review_finalized}>Formal review finalized</ChkItem>
              <ChkItem ok={chk.final_pdf_saved}>Final PDF link saved</ChkItem>
              <ChkItem ok={chk.pnc_marked_in_risk_matrix}>PNC marked in Risk Matrix (if applicable)</ChkItem>
              <ChkItem ok={chk.legal_hold_placed}>Legal hold placed on requirement(s)</ChkItem>
              <ChkItem ok={chk.preservation_recorded}>Evidence preserved (SharePoint / SEL)</ChkItem>
            </ul>
            <p className="text-xs text-muted-foreground mt-2">The Lead can close the investigation (status → closed) once every item is complete.</p>
          </div>
        </div>
      )}
    </div>
  );
}
